<script lang="ts">
import {defineComponent, ref, watch, toRefs, type PropType} from 'vue';
import type {ApiOffer, ApiOfferSubArray, RcaInsuranceCompany} from '~/types/Rca';
import {translation, useTranslationStore} from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    insuranceCompany: {
      type: Object as () => RcaInsuranceCompany,
      required: true
    },
    offer: {
      type: Array as () => Array<ApiOfferSubArray> | null,
      default: () => null
    },
    isDeducted: {
      type: Boolean as PropType<boolean | null>,
      default: false
    },
    availability: {
      type: [Array, String, Number] as PropType<Array<any> | string | number | null>,
      default: null
    }
  },
  setup(props, {emit}) {
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});

    const {offer, isDeducted} = toRefs(props);
    const firstOffer = ref(<ApiOfferSubArray>{});
    const secondOffer = ref(<ApiOfferSubArray>{});
    const secondAvailability = 2;

    const updateOffers = (newOffer: Array<ApiOfferSubArray>, isWithDeductedPrice: boolean) => {
      firstOffer.value = newOffer[0].find((o: any) => !!o.is_deducted === isWithDeductedPrice) || {};
      if (newOffer.length === secondAvailability) {
        secondOffer.value = newOffer[1].find((o: any) => !!o.is_deducted === isWithDeductedPrice) || {};
      } else {
        secondOffer.value = {};
      }
    };

    watch(offer, (newOffer) => {
      if (newOffer) updateOffers(newOffer, !!isDeducted.value);
    });

    watch(isDeducted, (newIsDeducted) => {
      updateOffers(<ApiOfferSubArray[]>offer.value, !!newIsDeducted);
    });

    onMounted(async () => {
      if (offer.value) updateOffers(offer.value, !!isDeducted.value);
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
    });

    const onSelectedOffer = (selectedOffer: ApiOffer) => {
      emit('selectedOffer', selectedOffer);
    };

    return {
      translationList,
      translation,
      firstOffer,
      secondOffer,
      onSelectedOffer,
      isDeducted
    };
  }
});
</script>

<template>
  <v-card class="d-flex justify-center" rounded="lg" color="gray-dark" variant="flat" min-height="175">
    <v-row class="my-1 px-3 d-flex align-center justify-start">
      <v-col cols="12" sm="12" md="7" class="d-flex flex-column centered-column-on-mobile">
        <div style="width: 200px; height: 80px;" class="d-flex align-center justify-center">
          <NuxtImg :src="'/images/companies/insurance/thumbnails/' + insuranceCompany.code + '.webp'"
                   :alt="insuranceCompany.name" class="pa-1 w-100"/>
        </div>
        <div style="width: 200px;" class="text-center">
          {{ translation(translationList.malus_bonus, 'Bonus Malus') }}: {{ firstOffer ? firstOffer.class_bm : '-' }}
        </div>
      </v-col>
      <v-col cols="12" sm="12" md="5" class="centered-column-on-mobile">
        <v-row>
          <v-col cols="12" sm="12" md="12" class="py-1" v-if="firstOffer && firstOffer.amount">
            <v-card rounded="lg" flat link @click="onSelectedOffer(firstOffer)">
              <div class="pa-2">
                <v-row class="d-flex align-center justify-space-between">
                  <v-col cols="4" sm="4" class="text-start">
                    <div class="fw-700 fs-18">{{ firstOffer.rca_availability_id }}
                      {{ translation(translationList.months, 'Luni') }}</div>
                  </v-col>
                  <v-col cols="8" sm="8" class="d-inline-flex justify-end align-center">
                    <div class="fw-700 fs-18 color-primary me-2">{{ firstOffer.amount }} lei</div>
                    <v-btn
                        color="primary"
                        min-width="0"
                        size="small"
                        variant="flat"
                        rounded="lg"
                        icon="mdi-cart"
                    >
                    </v-btn>
                  </v-col>
                </v-row>
                <v-tooltip
                    activator="parent"
                    location="top"
                >
                  <span>{{ translation(translationList.add_to_cart, 'Adaugă în coș') }}</span>
                </v-tooltip>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="12" md="12" class="py-1" v-if="secondOffer && secondOffer.amount">
            <v-card rounded="lg" flat link @click="onSelectedOffer(secondOffer)">
              <div class="pa-2">
                <v-row class="d-flex align-center justify-space-between">
                  <v-col cols="4" sm="4" class="text-start">
                    <div class="fw-700 fs-18">{{ secondOffer.rca_availability_id }}
                      {{ translation(translationList.months, 'Luni') }}</div>
                  </v-col>
                  <v-col cols="8" sm="8" class="d-inline-flex justify-end align-center">
                    <div class="fw-700 fs-18 color-primary me-2">{{ secondOffer.amount }} lei</div>
                    <v-btn
                        color="primary"
                        min-width="0"
                        size="small"
                        variant="flat"
                        rounded="lg"
                        icon="mdi-cart"
                    >
                    </v-btn>
                  </v-col>
                </v-row>
                <v-tooltip
                    activator="parent"
                    location="top"
                >
                  <span>{{ translation(translationList.add_to_cart, 'Adaugă în coș') }}</span>
                </v-tooltip>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<style scoped></style>
